import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import classnames from 'classnames';
import pluralize from 'pluralize';

import slugify from 'common/util/slugify';

import {
  USER_ROLE_ADMIN,
  USER_ROLE_ANONYMOUS,
} from 'common/userRoles';

import {
  numberWithCommas,
  bedsDisplayString,
  bathsDisplayString,
  formatPrice,
} from 'common/formatters';

import {
  LISTING_STATUS_ACTIVE,
  LISTING_DATA_SOURCE_SMART_APARTMENT,
  LISTING_PHOTO_DIMENSION_CARD,
} from 'common/listingConstants';

import {
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
} from 'common/householdConstants';

import {
  getListingFieldsForDisplay,
  getListingUrlPath,
  getListingManagedPhotoUrls,
  getDaysOnMarket,
  isSelfShowingListing,
  getListingAvailableNowLabel,
  hasListing3DTour,
  isListingTenantScreening,
} from 'common/listingHelpers';
import {
  isConcessionActive,
  calculateNetEffectiveRent,
} from 'common/concessionHelpers';
import {
  isRenterQualifiedForListing,
} from 'common/householdApplication/prequalificationHelpers';
import branch from 'client/core/branch';

import analytics from 'client/analytics';
import {
  ANALYTICS_EVENT_LISTING_PHOTO_CLICK,
} from 'client/analytics/eventList';

import UserActions from 'client/actions/User';

import SrTag, {
  SR_TAG_THEME_GREEN_WHITE,
} from 'client/components/SrTag/SrTag';
import SrLink from 'client/components/SrLink/SrLink';
import SrExternalLink from 'client/components/SrExternalLink';
import SrPhotosCarousel from 'client/components/SrPhotosCarousel';
import SrApplicationButton from 'client/components/SrApplicationButton';
import SrRequestShowingButton from 'client/components/SrRequestShowingButton';
import SrListingCardThumbnailHeader from 'client/components/SrListingCardThumbnailHeader';
import SrBatteryMessage from 'client/components/SrBatteryMessage';
import SrBox from './SrBox';

@withRouter
@branch({
  bookmarksByListingId: ['bookmarksByListingId'],
  appUrl: ['env', 'APP_URL'],
})
class ListingCard extends Component {
  static defaultProps = {
    showAvailableDate: true,
  }

  static propTypes = {
    useSrExternalLink: PropTypes.bool,
    viewText: PropTypes.string,
    listing: PropTypes.object.isRequired,
    applicationStatus: PropTypes.string,
    showRequestShowingButton: PropTypes.bool,
    enableBookmarkButton: PropTypes.bool,
    specialDeals: PropTypes.array,
    currentUser: PropTypes.object.isRequired,
    style: PropTypes.object,
    agentTourId: PropTypes.number,
    adminTourId: PropTypes.number,
    photoResolution: PropTypes.number.isRequired,
    bookmarksByListingId: PropTypes.object,
    requestedFrom: PropTypes.string,
    applicationStatusMessageOverride: PropTypes.string,
    showThumbnailListingAddress: PropTypes.bool,
    showThumbnailApartmentUnit: PropTypes.bool,
    showAvailableDate: PropTypes.bool,
    disableDefaultApplicationStatusMessage: PropTypes.bool,
    disablePhotoCarousel: PropTypes.bool,
    managementCompanyIframe: PropTypes.string,
    className: PropTypes.string,
    appUrl: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    noMaxWidth: PropTypes.bool,
  }

  static defaultProps = {
    viewText: 'View',
  }

  constructor(...args) {
    super(...args);
    this.state = {
      preloadAllSlideshowImages: false,
    };
  }

  _onPreviousPhoto = (photoIndex) => {
    analytics.track(ANALYTICS_EVENT_LISTING_PHOTO_CLICK, {
      photoIndex,
      slideshowDirection: 'left',
      listingId: this.props.listing.id,
    });
    this.setState({
      preloadAllSlideshowImages: true,
    });
  }

  _onNextPhoto = (photoIndex) => {
    analytics.track(ANALYTICS_EVENT_LISTING_PHOTO_CLICK, {
      photoIndex,
      slideshowDirection: 'right',
      listingId: this.props.listing.id,
    });
    this.setState({
      preloadAllSlideshowImages: true,
    });
  }

  _onBookmarkClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const {
      listing,
      currentUser,
      bookmarksByListingId,
      history,
      dispatch,
    } = this.props;
    const isBookmarked = bookmarksByListingId ? !!bookmarksByListingId[listing.id] : false;

    if (currentUser.roles.includes(USER_ROLE_ANONYMOUS)) {
      history.push(`/login?bookmarkRequested=${listing.id}`);
    }
    else if (isBookmarked) {
      dispatch(UserActions.removeBookmark, { listingId: listing.id });
    }
    else {
      dispatch(UserActions.addBookmark, { listingId: listing.id });
    }

    this.setState({
      preloadAllSlideshowImages: true,
    });
  }

  render() {
    const {
      useSrExternalLink,
      viewText,
      listing,
      applicationStatus,
      currentUser,
      style,
      showRequestShowingButton,
      agentTourId,
      adminTourId,
      photoResolution,
      specialDeals,
      bookmarksByListingId,
      enableBookmarkButton,
      requestedFrom,
      applicationStatusMessageOverride,
      showThumbnailListingAddress,
      showThumbnailApartmentUnit,
      showAvailableDate,
      disableDefaultApplicationStatusMessage,
      className,
      renterProfile,
      disablePhotoCarousel,
      managementCompanyIframe,
      appUrl,
      onClick,
      noMaxWidth,
    } = this.props;

    const isBookmarked = bookmarksByListingId ? !!bookmarksByListingId[listing.id] : false;
    const isRenterQualified = renterProfile
      && renterProfile.isPrequalCompleted
      && isRenterQualifiedForListing(renterProfile, listing);

    const {
      preloadAllSlideshowImages,
    } = this.state;

    const managedPhotoUrls = getListingManagedPhotoUrls({
      listing: listing,
      dimension: LISTING_PHOTO_DIMENSION_CARD,
      resolution: photoResolution,
    });

    const photoUrls = [
      ...managedPhotoUrls,
      ...(listing.apartmentCommunityPhotoUrls || []),
    ];

    const listingFieldsForDisplay = getListingFieldsForDisplay({
      listing: listing,
      searchFilters: currentUser.searchFilters,
    });

    let listingUrl = getListingUrlPath(listing, { agentTourId, requestedFrom, adminTourId });
    let target = null;
    if (managementCompanyIframe) {
      listingUrl = (
        `${appUrl}${listingUrl}?utm_source=property-manager&utm_medium=iframe-widget` +
        `&utm_campaign=${encodeURIComponent(slugify(managementCompanyIframe))}&utm_content=listing-card`
      );
      target = '_blank';
    }

    const isLeased = listing.status !== LISTING_STATUS_ACTIVE;
    const completedApp = applicationStatus === HOUSEHOLD_APPLICATION_STATUS_COMPLETED;
    const daysOnMarket = getDaysOnMarket(listing);
    const shouldShowTags = (
      !showThumbnailListingAddress
      && !showThumbnailApartmentUnit
    );

    let tourButtonDOM;
    if (showRequestShowingButton && listing.dataSource !== LISTING_DATA_SOURCE_SMART_APARTMENT) {
      tourButtonDOM = (
        <SrRequestShowingButton
          requestedFrom={requestedFrom}
          listing={listing}
        />
      );
    }

    let Link = useSrExternalLink ? SrExternalLink : SrLink;
    let linkProps = useSrExternalLink
      ? { className: listing.dataSource, link: listingUrl }
      : { className: listing.dataSource, to: listingUrl, target: target };

    if (isListingTenantScreening(listing)) {
      Link = SrBox;
      linkProps = {};
    }

    const hasActiveConcession = (
      listing.concession
      && isConcessionActive(listing.concession)
    );

    return (
      <div
        className={classnames(
          'searchLinkContainer',
          className,
          {
            leased: isLeased,
            applicationSubmitted: completedApp,
            noMaxWidth,
          }
        )}
        style={style}
        onClick={onClick}
      >
        {enableBookmarkButton && (
          <div
            onClick={this._onBookmarkClick}
            className={classnames(
              'listingBookmarkIcon',
              isBookmarked && 'active',
            )}
          />
        )}
        {specialDeals && specialDeals.length > 0 && (
          <div className='listingCardTags'>
            {specialDeals.map((specialDeal) => (
              <SrTag key={specialDeal.id} theme={SR_TAG_THEME_GREEN_WHITE} className='listingCardTag'>
                {specialDeal.headline}
              </SrTag>
            ))}
          </div>
        )}
        <Link {...linkProps}>
          <div className='searchResult'>
            <div className='listingContainer'>
              <div className='thumbnail'>
                <SrListingCardThumbnailHeader
                  applicationStatus={applicationStatus}
                  listing={listing}
                  applicationStatusMessageOverride={applicationStatusMessageOverride}
                  showListingAddress={showThumbnailListingAddress}
                  showApartmentUnit={showThumbnailApartmentUnit}
                  disableDefaultApplicationStatusMessage={disableDefaultApplicationStatusMessage}
                />
                {!disablePhotoCarousel && (
                  <SrPhotosCarousel
                    photos={photoUrls}
                    preloadedPhotos={preloadAllSlideshowImages
                      ? photoUrls
                      : photoUrls.slice(0, 3)
                    }
                    onPreviousPhoto={this._onPreviousPhoto}
                    onNextPhoto={this._onNextPhoto}
                    textOverlay={listing.addressStreetDisplay}
                  />
                )}
                {disablePhotoCarousel && (
                  <div className='SrListingPhotosCarousel'>
                    <div className='imageContainer'>
                      <img src={photoUrls[0]} alt='Listing' />
                    </div>
                  </div>
                )}
                <div className='thumbnailTags'>
                  {renterProfile
                    && renterProfile.isPrequalCompleted
                    && listing.dataSource !== LISTING_DATA_SOURCE_SMART_APARTMENT
                    && isRenterQualified
                    && (
                      <div className='thumbnailQualificationLabel'>
                        <SrBatteryMessage
                          message='Qualified'
                          isNegative={false}
                          small
                        />
                      </div>
                    )}
                  {isSelfShowingListing(listing) && shouldShowTags && (
                    <div className='selfTourTag' />
                  )}
                  {typeof daysOnMarket === 'number' && daysOnMarket <= 3 && shouldShowTags && (
                    <div className='availableNowTag' />
                  )}
                  {hasActiveConcession && (
                    <div className='concessionTag'>
                      <span>Rent special</span>
                    </div>
                  )}
                  {hasListing3DTour(listing) && shouldShowTags && (
                    <div className='virtual3DTourTag' />
                  )}
                </div>
              </div>
              <div className='previewContent'>
                <span className='price'>
                  {formatPrice(hasActiveConcession
                    ? (
                      calculateNetEffectiveRent({
                        concession: listing.concession,
                        rentPrice: listing.price,
                      }).netEffectiveRent
                    )
                    : listingFieldsForDisplay.price
                  )}
                </span>
                {listing.pinned && currentUser.roles.includes(USER_ROLE_ADMIN) && (
                  <span
                    className='pin'
                    role='img'
                    aria-label='pinned-label'
                  >
                    📌
                  </span>
                )}
                {showAvailableDate && (
                  <span className='dateListed'>
                    {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                      ? 'Updated daily'
                      : getListingAvailableNowLabel(listing)
                    }
                  </span>
                )}
                <div className='specifics'>
                  {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                    ? (
                      <span>
                        {pluralize('unit layout', listing.apartmentUnits.length, true)}
                      </span>
                    ) : (
                      <span>
                        <span>{bedsDisplayString(listingFieldsForDisplay)}</span>
                        <span className='pipe'> | </span>
                        <span>{bathsDisplayString(listingFieldsForDisplay)}</span>
                        <span className='pipe'> | </span>
                        <span>
                          {numberWithCommas(listingFieldsForDisplay.squareFeet)} ft<sup>&#178;</sup>
                        </span>
                      </span>
                    )}
                  <span className='view'>{viewText}</span>
                </div>
                {!isLeased
                  && (applicationStatus && !completedApp)
                  && (
                    <div>
                      <SrApplicationButton
                        listing={listing}
                      />
                    </div>
                  )}
                <div>
                  {tourButtonDOM}
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  }
}

ListingCard.displayName = 'ListingCard';

export default ListingCard;
